import { getToken } from "firebase/messaging";
import { getFirebaseMessaging, VAPID_KEY } from "./config";
import { ensureEffectivelyOnline } from "./connectivity";
import { apiFetchClient } from "../api/client";
import { endpoints } from "../api/endpoints";

const FCM_TOKEN_KEY_PREFIX = "a2z_fcm_token";

/** Max time we allow `getToken` to run before treating it as a transient timeout. */
const GET_TOKEN_TIMEOUT_MS = 10_000;

function storageKey(storeId?: string): string {
  return storeId ? `${FCM_TOKEN_KEY_PREFIX}_${storeId}` : FCM_TOKEN_KEY_PREFIX;
}

/**
 * Classified reason a token request failed. Surfaced to the provider (Phase 2)
 * so it can decide whether to retry (transient) or stop (terminal).
 *  - `config`     — Firebase messaging / VAPID not configured (terminal).
 *  - `offline`    — browser offline or Firebase Installations app-offline (transient).
 *  - `network`    — fetch/network failure reaching FCM (transient).
 *  - `timeout`    — `getToken` exceeded the timeout (transient).
 *  - `permission` — notification permission blocked/denied (terminal).
 *  - `unknown`    — anything else (transient by default at the provider).
 */
export type FcmErrorReason =
  | "config"
  | "offline"
  | "network"
  | "timeout"
  | "permission"
  | "unknown";

/** Discriminated result of a token request. */
export type FcmTokenResult =
  | { ok: true; token: string }
  | { ok: false; reason: FcmErrorReason; error?: unknown };

/** Tag used so the timeout branch of `Promise.race` is identifiable. */
const FCM_TIMEOUT_TAG = "fcm-get-token-timeout";

/** Defensive, type-safe reads of common error shapes (no `any`). */
function errorCode(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

function errorName(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "name" in error) {
    const name = (error as { name?: unknown }).name;
    return typeof name === "string" ? name : undefined;
  }
  return undefined;
}

function errorMessage(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : undefined;
  }
  return undefined;
}

/**
 * Map a thrown error to a {@link FcmErrorReason}. Reads `.code`/`.name`/`.message`
 * defensively. Ordering matters: offline is checked before generic network because
 * the Installations SDK throws `installations/app-offline` when `navigator.onLine`
 * is false (the root cause this change heals).
 */
export function classifyFcmError(error: unknown): FcmErrorReason {
  const code = errorCode(error);
  const name = errorName(error);
  const message = errorMessage(error);

  // Offline: Firebase Installations app-offline, or the browser reports offline.
  if (
    code === "installations/app-offline" ||
    (typeof navigator !== "undefined" && navigator.onLine === false)
  ) {
    return "offline";
  }

  // Permission blocked/denied.
  if (
    code === "messaging/permission-blocked" ||
    code === "messaging/permission-default" ||
    name === "NotAllowedError"
  ) {
    return "permission";
  }

  // Network failure reaching FCM (fetch rejects with a TypeError).
  if (
    name === "TypeError" ||
    (typeof message === "string" && /failed to fetch|network/i.test(message))
  ) {
    return "network";
  }

  return "unknown";
}

/**
 * Request an FCM registration token.
 *
 * Single attempt by design: the central FcmRegistrationProvider (Phase 2) owns
 * retry/backoff so we avoid double-retry here. The `getToken` call is wrapped in
 * a timeout via `Promise.race` because the Firebase SDK call has no built-in
 * timeout and can hang on a stuck service worker / push endpoint.
 *
 * Returns a discriminated result so callers can react to the failure reason
 * instead of a bare null.
 */
export async function requestFcmToken(): Promise<FcmTokenResult> {
  // The service worker is only emitted for production builds (VitePWA
  // `devOptions.enabled: false`), so registering it in dev fetches the SPA
  // fallback HTML and logs "unsupported MIME type ('text/html')" on every
  // load. PwaBootstrap already sits this out in dev; do the same here.
  if (!import.meta.env.PROD) {
    return { ok: false, reason: "config" };
  }

  const messaging = getFirebaseMessaging();
  if (!messaging || !VAPID_KEY) {
    return { ok: false, reason: "config" };
  }

  // `navigator.onLine` is unreliable and can be stuck `false` while the network
  // actually works; Firebase's Installations SDK trusts it and throws
  // `installations/app-offline`. Don't trust a `false` flag — probe real
  // connectivity, and if we're genuinely online, force the flag so getToken can
  // proceed. Only a probe failure (truly offline) short-circuits here.
  if (!(await ensureEffectivelyOnline())) {
    return { ok: false, reason: "offline" };
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    const reg = await navigator.serviceWorker.register("/seller-sw.js");

    const tokenPromise = getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: reg,
    });

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(FCM_TIMEOUT_TAG));
      }, GET_TOKEN_TIMEOUT_MS);
    });

    const token = await Promise.race([tokenPromise, timeoutPromise]);

    if (!token) {
      // SDK resolved with an empty token — treat as unknown transient failure.
      console.warn("[fcm] getToken returned empty token");
      return { ok: false, reason: "unknown" };
    }

    return { ok: true, token };
  } catch (error) {
    if (error instanceof Error && error.message === FCM_TIMEOUT_TAG) {
      console.warn("[fcm] getToken timed out", {
        reason: "timeout",
        timeoutMs: GET_TOKEN_TIMEOUT_MS,
      });
      return { ok: false, reason: "timeout" };
    }

    const reason = classifyFcmError(error);
    console.warn("[fcm] getToken failed", { reason, error });
    return { ok: false, reason, error };
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

/**
 * POST the token to the backend. The backend derives the store from the JWT, so
 * the body stays `{ token }` (storeId is NOT sent). `apiFetchClient` returns a
 * `Response` and only rejects on a network error (never on a non-2xx), so success
 * is gated on `response.ok`.
 *
 * Returns `true` only on a 2xx. The localStorage token cache is updated ONLY on
 * success so a failed POST is not mistaken for a registered token.
 */
export async function registerFcmTokenWithBackend(
  token: string,
  storeId?: string,
): Promise<boolean> {
  try {
    const response = await apiFetchClient(endpoints.seller.fcmTokens, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      console.warn("[fcm] token registration failed", {
        status: response.status,
      });
      return false;
    }

    localStorage.setItem(storageKey(storeId), token);
    return true;
  } catch (error) {
    console.warn("[fcm] token registration request failed", { error });
    return false;
  }
}

export async function deregisterFcmTokenFromBackend(
  storeId?: string,
): Promise<void> {
  const token = localStorage.getItem(storageKey(storeId));
  if (!token) return;
  try {
    await apiFetchClient(endpoints.seller.fcmTokens, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
  } catch (error) {
    console.warn("FCM token deregistration failed", error);
  } finally {
    localStorage.removeItem(storageKey(storeId));
  }
}

export function getStoredFcmToken(storeId?: string): string | null {
  return localStorage.getItem(storageKey(storeId));
}
